"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    regions: Record<string, string>;
    overlapHeading: string;
    overlapSub: string;
    allCountry: string;
    usLargeCap: string;
    thisOverlaps: string;
    doubleHolding: string;
    overlapTitle: string;
    overlapDescription: string;
    columns: { label: string; subLabel: string }[];
    shiftHeading: string;
    shiftSub: string;
    nonUs: (pct: number) => string;
    us: string;
    shiftNote: string;
    shiftTitle: string;
    shiftDescription: string;
  }
> = {
  ja: {
    regions: { us: "米国", europe: "欧州", emerging: "新興国", japan: "日本", other: "その他" },
    overlapHeading: "全世界株式と S&P500 の中身を並べて見る",
    overlapSub: "米国部分がどれだけ重なるかを確認できます",
    allCountry: "全世界株式",
    usLargeCap: "米国大型株",
    thisOverlaps: "この部分が重なる（主要銘柄が二重に）",
    doubleHolding: "両方持つと、Apple・Microsoft・Amazon などを二重に持つ形になりやすい",
    overlapTitle: "中身を並べると、重なりが見える",
    overlapDescription:
      "全世界株式の米国部分と S&P500 はほぼ同じ銘柄群です。両方持つと主要銘柄を二重に持つ形になります。",
    columns: [
      { label: "全世界株式のみ", subLabel: "分散のベース" },
      { label: "全世界 50% + S&P500 50%", subLabel: "よくある併用パターン" },
      { label: "全世界 30% + S&P500 70%", subLabel: "S&P500 を多めに持つ場合" },
    ],
    shiftHeading: "S&P500 を足すと、米国比率はどこまで増えるか",
    shiftSub: "併用の比率を変えたときの米国ウェイトの変化",
    nonUs: (pct) => `米国以外 ${pct}%`,
    us: "米国",
    shiftNote: "「分散のために2本持つ」のではなく「米国を厚く持つ」という意味合い",
    shiftTitle: "S&P500 を足すほど、米国比率が上がる",
    shiftDescription:
      "全世界株式だけなら米国は約6割ですが、S&P500 を半分ずつ持つだけで約8割になります。",
  },
  en: {
    regions: { us: "US", europe: "Europe", emerging: "Emerging", japan: "Japan", other: "Other" },
    overlapHeading: "Placing global equities and the S&P 500 side by side",
    overlapSub: "You can see how much the US portion overlaps",
    allCountry: "Global equities",
    usLargeCap: "US large caps",
    thisOverlaps: "This part overlaps (major names held twice)",
    doubleHolding: "Hold both and you tend to hold Apple, Microsoft, and Amazon twice over",
    overlapTitle: "Line up the contents and the overlap becomes visible",
    overlapDescription:
      "The US portion of a global equity fund and the S&P 500 are largely the same set of names. Hold both and you hold the major names twice.",
    columns: [
      { label: "Global equities only", subLabel: "The diversification base" },
      { label: "50% global + 50% S&P 500", subLabel: "A common pairing" },
      { label: "30% global + 70% S&P 500", subLabel: "Weighting the S&P 500 more heavily" },
    ],
    shiftHeading: "How far does adding the S&P 500 push up the US weight?",
    shiftSub: "How the US weight changes as you vary the pairing",
    nonUs: (pct) => `Non-US ${pct}%`,
    us: "US",
    shiftNote: "This is less “holding two funds to diversify” and more “holding more US”",
    shiftTitle: "The more S&P 500 you add, the higher the US weight",
    shiftDescription:
      "With global equities alone the US is about 60%; holding the S&P 500 as half takes it to about 80%.",
  },
};

// ── Colors ────────────────────────────────────────────────────────

const C = {
  us: COLOR.sky,
  europe: COLOR.emerald,
  japan: COLOR.rose,
  emerging: "#7c3aed",
  other: COLOR.amber,
  overlap: "#dc2626",
  sp500: "#2563eb",
  allCountry: COLOR.emerald,
};

// ── Shared SVG helpers ────────────────────────────────────────────

function RoundedBar({
  x,
  y,
  w,
  h,
  color,
  opacity = 0.18,
  strokeOpacity = 1,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  opacity?: number;
  strokeOpacity?: number;
}) {
  return (
    <rect
      x={x}
      y={y}
      width={Math.max(w, 0)}
      height={h}
      rx={3}
      fill={color}
      fillOpacity={opacity}
      stroke={color}
      strokeWidth="1"
      strokeOpacity={strokeOpacity}
    />
  );
}

function BarLabel({
  x,
  y,
  label,
  pct,
  color,
  fontSize = 8,
}: {
  x: number;
  y: number;
  label: string;
  pct: string;
  color: string;
  fontSize?: number;
}) {
  return (
    <g>
      <text x={x} y={y} textAnchor="middle" fontSize={fontSize} fill={color} fontWeight="600">
        {label}
      </text>
      <text x={x} y={y + fontSize + 2} textAnchor="middle" fontSize={fontSize + 1} fill={color} fontWeight="700">
        {pct}
      </text>
    </g>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 1: Overlap between All-Country and S&P500
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type Segment = { key: string; pct: number; color: string };

const ALL_COUNTRY_SEGMENTS: Segment[] = [
  { key: "us", pct: 63, color: C.us },
  { key: "europe", pct: 15, color: C.europe },
  { key: "emerging", pct: 10, color: C.emerging },
  { key: "japan", pct: 5, color: C.japan },
  { key: "other", pct: 7, color: C.other },
];

function StackedBar({
  x,
  y,
  totalW,
  h,
  segments,
}: {
  x: number;
  y: number;
  totalW: number;
  h: number;
  segments: Segment[];
}) {
  const t = STRINGS[useDocLocale()];
  let offset = 0;
  return (
    <g>
      {segments.map((seg) => {
        const w = (seg.pct / 100) * totalW;
        const sx = x + offset;
        offset += w;
        const showLabel = w > 20;
        return (
          <g key={seg.key}>
            <RoundedBar x={sx} y={y} w={w} h={h} color={seg.color} opacity={0.22} />
            {showLabel ? (
              <BarLabel
                x={sx + w / 2}
                y={y + h / 2 - 4}
                label={t.regions[seg.key]!}
                pct={`${seg.pct}%`}
                color={seg.color}
                fontSize={7}
              />
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

function OverlapIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 210;
  const barW = 380;
  const barH = 38;
  const leftPad = 10;
  const barX = leftPad + 60;

  // Row positions
  const acY = 58;
  const spY = 120;
  const overlapArrowY = spY + barH + 14;

  // US portion of All-Country
  const usWidth = (63 / 100) * barW;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title labels */}
      <text x={VW / 2} y={20} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.overlapHeading}
      </text>
      <text x={VW / 2} y={34} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.overlapSub}
      </text>

      {/* All-Country bar */}
      <text x={leftPad} y={acY + barH / 2 + 3} fontSize="9" fill="currentColor" fontWeight="600">
        {t.allCountry}
      </text>
      <StackedBar x={barX} y={acY} totalW={barW} h={barH} segments={ALL_COUNTRY_SEGMENTS} />

      {/* S&P500 bar */}
      <text x={leftPad} y={spY + barH / 2 + 3} fontSize="9" fill="currentColor" fontWeight="600">
        S&P500
      </text>
      <RoundedBar x={barX} y={spY} w={barW} h={barH} color={C.us} opacity={0.22} />
      <BarLabel x={barX + barW / 2} y={spY + barH / 2 - 4} label={t.usLargeCap} pct="100%" color={C.us} fontSize={8} />

      {/* Overlap bracket connecting US portions */}
      <line
        x1={barX}
        y1={acY + barH + 3}
        x2={barX}
        y2={spY - 3}
        stroke={C.overlap}
        strokeWidth="1"
        strokeDasharray="3 3"
        opacity={0.5}
      />
      <line
        x1={barX + usWidth}
        y1={acY + barH + 3}
        x2={barX + usWidth}
        y2={spY - 3}
        stroke={C.overlap}
        strokeWidth="1"
        strokeDasharray="3 3"
        opacity={0.5}
      />
      {/* Overlap shading */}
      <rect
        x={barX}
        y={acY + barH + 3}
        width={usWidth}
        height={spY - acY - barH - 6}
        fill={C.overlap}
        fillOpacity={0.06}
      />
      <text
        x={barX + usWidth / 2}
        y={(acY + barH + spY) / 2 + 3}
        textAnchor="middle"
        fontSize="8"
        fill={C.overlap}
        fontWeight="600"
      >
        {t.thisOverlaps}
      </text>

      {/* Bottom annotation */}
      <text x={VW / 2} y={overlapArrowY + 16} textAnchor="middle" fontSize="9.5" fill={C.overlap} fontWeight="600">
        {t.doubleHolding}
      </text>
    </svg>
  );
}

export function DocAllCountryOverlapDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.overlapTitle}
      description={t.overlapDescription}
    >
      <OverlapIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 2: US weight shift when combining
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type BarColumn = {
  labelIndex: number;
  usPct: number;
  otherPct: number;
};

const COLUMNS: BarColumn[] = [
  {
    labelIndex: 0,
    usPct: 63,
    otherPct: 37,
  },
  {
    labelIndex: 1,
    usPct: 81,
    otherPct: 19,
  },
  {
    labelIndex: 2,
    usPct: 89,
    otherPct: 11,
  },
];

function USWeightShiftIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 255;
  const colW = 110;
  const gap = 30;
  const totalW = COLUMNS.length * colW + (COLUMNS.length - 1) * gap;
  const startX = (VW - totalW) / 2;
  const barMaxH = 150;
  const barY = 70;
  const barW = colW;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={20} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.shiftHeading}
      </text>
      <text x={VW / 2} y={34} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.shiftSub}
      </text>

      {COLUMNS.map((col, i) => {
        const cx = startX + i * (colW + gap) + colW / 2;
        const bx = startX + i * (colW + gap);

        const usH = (col.usPct / 100) * barMaxH;
        const otherH = barMaxH - usH;

        // US portion (bottom)
        const usY = barY + otherH;
        // Other portion (top)
        const otherY = barY;

        // Color intensity increases with index
        const usOpacity = 0.18 + i * 0.08;

        return (
          <g key={col.labelIndex}>
            {/* Column label */}
            <text x={cx} y={barY - 14} textAnchor="middle" fontSize="8" fill="currentColor" fontWeight="600">
              {t.columns[col.labelIndex]!.label}
            </text>
            <text x={cx} y={barY - 4} textAnchor="middle" fontSize="7" fill="currentColor" fillOpacity={0.5}>
              {t.columns[col.labelIndex]!.subLabel}
            </text>

            {/* Other bar (top) */}
            {otherH > 4 ? (
              <g>
                <RoundedBar x={bx} y={otherY} w={barW} h={otherH} color={C.allCountry} opacity={0.14} />
                {otherH > 18 ? (
                  <text
                    x={cx}
                    y={otherY + otherH / 2 + 3}
                    textAnchor="middle"
                    fontSize="8"
                    fill={C.allCountry}
                    fontWeight="600"
                  >
                    {t.nonUs(col.otherPct)}
                  </text>
                ) : (
                  <text
                    x={cx}
                    y={otherY + otherH / 2 + 3}
                    textAnchor="middle"
                    fontSize="7"
                    fill={C.allCountry}
                    fontWeight="600"
                  >
                    {col.otherPct}%
                  </text>
                )}
              </g>
            ) : null}

            {/* US bar (bottom) */}
            <RoundedBar x={bx} y={usY} w={barW} h={usH} color={C.us} opacity={usOpacity} />
            <text x={cx} y={usY + usH / 2 - 2} textAnchor="middle" fontSize="9" fill={C.us} fontWeight="600">
              {t.us}
            </text>
            <text x={cx} y={usY + usH / 2 + 11} textAnchor="middle" fontSize="13" fill={C.us} fontWeight="700">
              {col.usPct}%
            </text>

            {/* Arrow between columns */}
            {i < COLUMNS.length - 1 ? (
              <g>
                <line
                  x1={bx + barW + 6}
                  y1={barY + barMaxH / 2}
                  x2={bx + barW + gap - 6}
                  y2={barY + barMaxH / 2}
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeOpacity={0.2}
                  markerEnd="url(#arrowHead)"
                />
              </g>
            ) : null}
          </g>
        );
      })}

      {/* Arrow marker */}
      <defs>
        <marker id="arrowHead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity={0.25} />
        </marker>
      </defs>

      {/* Bottom message */}
      <text x={VW / 2} y={barY + barMaxH + 28} textAnchor="middle" fontSize="9.5" fill={C.overlap} fontWeight="600">
        {t.shiftNote}
      </text>
    </svg>
  );
}

export function DocUSWeightShiftDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.shiftTitle}
      description={t.shiftDescription}
    >
      <USWeightShiftIllustration />
    </IllustrationCard>
  );
}
